<script setup lang="ts">
import { computed, ref } from 'vue';
import DetailsSheet from '@/components/editing/DetailsSheet.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Check, ChevronDown, ChevronUp, Clipboard, Maximize2, MoreHorizontal, Sparkles, ThumbsDown, ThumbsUp, Trash2 } from 'lucide-vue-next';
import type { AssistantFeedbackRating } from '~/composables/useAssistantFeedback';
import type { AssistantSuggestionRefinementInput } from '~/lib/assistantSuggestionUi';
import { assistantConfidenceBadgeClass, assistantPriorityLabel } from '~/lib/assistantSuggestionUi';
import type { AssistantSuggestion } from '~/types/models';

const props = withDefaults(defineProps<{
    suggestion: AssistantSuggestion;
    applying?: boolean;
    refining?: boolean;
    expanded?: boolean;
    expandable?: boolean;
    compact?: boolean;
    collapsible?: boolean;
    defaultCollapsed?: boolean;
    canRefine?: boolean;
    evidenceItems?: string[];
}>(), {
    applying: false,
    refining: false,
    expanded: false,
    expandable: false,
    compact: false,
    collapsible: false,
    defaultCollapsed: false,
    canRefine: false,
    evidenceItems: () => [],
});

const emit = defineEmits<{
    (e: 'apply'): void;
    (e: 'dismiss'): void;
    (e: 'copy'): void;
    (e: 'toggle-expand'): void;
    (e: 'refine', payload: AssistantSuggestionRefinementInput): void;
    (e: 'feedback', rating: AssistantFeedbackRating): void;
}>();

const collapsed = ref(props.defaultCollapsed);
const refineDialogOpen = ref(false);
const refinementText = ref('');
const expandedState = computed(() => !collapsed.value);
const displayExpandedReply = computed(() => props.expandable && props.compact ? props.expanded : true);
const refinementInputId = computed(() => `assistant-reply-refinement-${props.suggestion.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`);
const feedbackRating = ref<AssistantFeedbackRating | null>(null);

function toggleExpanded() {
    if (!props.collapsible) return;
    collapsed.value = !collapsed.value;
}

function openRefineDialog() {
    refineDialogOpen.value = true;
}

function submitRefinement() {
    const instructions = refinementText.value.trim();
    if (!instructions) return;

    refineDialogOpen.value = false;
    refinementText.value = '';
    emit('refine', { instructions });
}

function submitFeedback(rating: AssistantFeedbackRating) {
    feedbackRating.value = rating;
    emit('feedback', rating);
}
</script>

<template>
    <div class="rounded-xl border border-border/60 bg-slate-100/95 p-2.5 dark:bg-[color:color-mix(in_srgb,var(--background)_86%,#020617_14%)]">
        <div class="flex items-start justify-between gap-2">
            <div class="space-y-1.5">
                <div class="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" class="border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200">
                        Draft Reply Ready
                    </Badge>
                    <Badge variant="outline" :class="assistantConfidenceBadgeClass(suggestion.confidence)">
                        {{ assistantPriorityLabel(suggestion.confidence) }}
                    </Badge>
                </div>
                <p class="text-sm text-muted-foreground" :class="expandedState ? '' : 'line-clamp-2'">{{ suggestion.summary }}</p>
            </div>

            <Button
                v-if="collapsible"
                size="icon"
                variant="ghost"
                class="mt-0.5 h-6 w-6 rounded-full border border-border/30 bg-white/80 text-muted-foreground hover:bg-accent/60 hover:text-foreground dark:bg-background/55"
                @click="toggleExpanded"
            >
                <ChevronUp v-if="expandedState" class="size-3.5" />
                <ChevronDown v-else class="size-3.5" />
                <span class="sr-only">{{ expandedState ? 'Collapse suggestion' : 'Expand suggestion' }}</span>
            </Button>
        </div>

        <template v-if="expandedState">
            <div v-if="evidenceItems.length" class="mt-2.5 border-t border-border/50 pt-2.5">
                <p class="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Based on</p>
                <ul class="mt-1.5 space-y-1.5">
                    <li
                        v-for="(evidence, index) in evidenceItems"
                        :key="`${suggestion.id}-evidence-${index}`"
                        class="border-l-2 border-border/70 pl-2 text-xs leading-5 text-muted-foreground"
                    >
                        “{{ evidence }}”
                    </li>
                </ul>
            </div>

            <div class="mt-2.5 rounded-xl bg-background/55 px-3 py-3 dark:bg-background/35">
                <p class="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Suggested reply</p>
                <p
                    class="text-sm text-foreground/90"
                    :class="compact && expandable && !displayExpandedReply ? 'line-clamp-3' : 'whitespace-pre-wrap'"
                >
                    {{ suggestion.suggestedReply }}
                </p>
            </div>

            <div class="mt-2.5 flex flex-wrap items-center gap-2">
                <DropdownMenu>
                    <DropdownMenuTrigger as-child>
                        <Button size="sm" :disabled="applying || refining">
                            Actions
                            <MoreHorizontal class="ml-1 size-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" class="w-52">
                        <DropdownMenuItem @select="$emit('apply')">
                            <Check class="size-4" />
                            Use reply
                        </DropdownMenuItem>
                        <DropdownMenuItem @select="$emit('copy')">
                            <Clipboard class="size-4" />
                            Copy reply
                        </DropdownMenuItem>
                        <DropdownMenuItem v-if="expandable" @select="$emit('toggle-expand')">
                            <Maximize2 class="size-4" />
                            {{ expanded ? 'Collapse draft' : 'Expand draft' }}
                        </DropdownMenuItem>
                        <DropdownMenuItem v-if="canRefine" @select="openRefineDialog">
                            <Sparkles class="size-4" />
                            Refine with AI
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" @select="$emit('dismiss')">
                            <Trash2 class="size-4" />
                            Dismiss suggestion
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
                <div class="ml-auto flex items-center gap-0.5" aria-label="Rate this suggestion">
                    <Button
                        size="icon"
                        variant="ghost"
                        class="size-7"
                        :class="feedbackRating === 'helpful' ? 'text-primary' : 'text-muted-foreground'"
                        :aria-pressed="feedbackRating === 'helpful'"
                        aria-label="Mark reply helpful"
                        @click="submitFeedback('helpful')"
                    >
                        <ThumbsUp class="size-3.5" />
                    </Button>
                    <Button
                        size="icon"
                        variant="ghost"
                        class="size-7"
                        :class="feedbackRating === 'not_helpful' ? 'text-destructive' : 'text-muted-foreground'"
                        :aria-pressed="feedbackRating === 'not_helpful'"
                        aria-label="Mark reply not helpful"
                        @click="submitFeedback('not_helpful')"
                    >
                        <ThumbsDown class="size-3.5" />
                    </Button>
                </div>
            </div>
        </template>

        <DetailsSheet
            :open="refineDialogOpen"
            title="Refine reply with AI"
            description="Tell the assistant how the reply should change before you use it."
            :dirty="Boolean(refinementText.trim())"
            :saving="refining"
            save-label="Update reply"
            saving-label="Updating…"
            :save-disabled="!refinementText.trim()"
            @update:open="refineDialogOpen = $event"
            @cancel="refinementText = ''"
            @save="submitRefinement"
        >
            <div class="space-y-2">
                <label :for="refinementInputId" class="text-sm font-medium">What should change?</label>
                <Textarea
                    :id="refinementInputId"
                    v-model="refinementText"
                    rows="5"
                    :maxlength="500"
                    :aria-describedby="`${refinementInputId}-help`"
                    class="min-h-32 resize-y"
                    placeholder="For example: make this warmer, shorten it, or ask a clearer follow-up question."
                />
                <div :id="`${refinementInputId}-help`" class="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                    <span>Updating uses one AI action.</span>
                    <span>{{ refinementText.length }} / 500</span>
                </div>
            </div>
        </DetailsSheet>
    </div>
</template>
